/**
 * Company branding API operations
 */
import { get, request, unwrapData } from './base';

export interface Company {
  id?: string;
  name: string;
  description: string;
  nda_required?: boolean;
  nda_template?: string;
  show_valuation?: boolean;
  valuation?: number;
  outstanding_shares?: number;
  show_comparable_companies?: boolean;
  include_benefits_in_comp_chart?: boolean;
  use_gross_equity_value?: boolean;
  /** Upper bound of the comp chart's growth-projection slider (manual moves only). */
  comp_chart_max_multiplier?: number;
  /** Custom disclaimer under the comp chart; empty means the standard copy. */
  comp_chart_disclaimer?: string;
  default_equity_type?: string;
  default_strike_price?: number;
  default_preferred_price?: number;
  default_payment_frequency?: string;
  default_employment_type?: string;
  default_offer_expiration_days?: number | null;
  default_welcome_message?: string | null;
  /** Default USD conversion rates keyed by currency code, e.g. `{ CAD: '1.37' }`. */
  default_conversion_rates?: Record<string, string> | null;
  default_conversion_rates_updated_at?: string | null;
  // Offer visibility settings for public shared links
  public_offer_show_compensation?: boolean;
  public_offer_show_equity?: boolean;
  public_offer_show_benefits?: boolean;
  public_offer_show_bonus?: boolean;
  public_offer_show_commission?: boolean;
  public_offer_show_total_comp_chart?: boolean;
  public_offer_show_comparable_companies?: boolean;
}

export interface CompanyBranding {
  id?: string;
  company?: Company;
  logo: string;
  header_image: string;
  primary_color: string;
  comparable_companies?: Array<{
    id?: string;
    name: string;
    stage: string;
    valuation: number;
    valuation_date?: string | null;
    private_notes?: string;
    public_comments?: string;
  }>;
}

export interface CompanyBrandingUpdateData {
  /**
   * The fields to change. This is a patch, not a replacement: the endpoint
   * merges, so a settings page should send only what it owns and omit the
   * rest. `company` is a Partial because a page rarely owns name/description.
   */
  formData: Partial<Omit<CompanyBranding, 'company'>> & { company?: Partial<Company> };
  logoFile?: File | null;
  headerImageFile?: File | null;
  removeLogo?: boolean;
  removeHeaderImage?: boolean;
}

/**
 * Get company branding information
 */
export const getCompanyBranding = async (): Promise<CompanyBranding> =>
  get<CompanyBranding>('/company/branding/');

/**
 * Update company branding information
 */
export const updateCompanyBranding = async (
  data: CompanyBrandingUpdateData
): Promise<CompanyBranding> => {
  // Create FormData for submission
  const formData = new FormData();

  // Add text fields for company
  if (data.formData.company?.name) {
    formData.append('company_name', data.formData.company.name);
  }

  if (data.formData.company?.description) {
    formData.append('description', data.formData.company.description);
  }

  // Add nda_required field
  if (typeof data.formData.company?.nda_required === 'boolean') {
    formData.append('nda_required', data.formData.company.nda_required ? 'true' : 'false');
  }

  // Add nda_template field
  if (data.formData.company?.nda_template) {
    formData.append('nda_template', data.formData.company.nda_template);
  }

  // Add show_valuation field
  if (typeof data.formData.company?.show_valuation === 'boolean') {
    formData.append('show_valuation', data.formData.company.show_valuation ? 'true' : 'false');
  }

  // Add show_comparable_companies field
  if (typeof data.formData.company?.show_comparable_companies === 'boolean') {
    formData.append(
      'show_comparable_companies',
      data.formData.company.show_comparable_companies ? 'true' : 'false'
    );
  }

  // Add include_benefits_in_comp_chart field
  if (typeof data.formData.company?.include_benefits_in_comp_chart === 'boolean') {
    formData.append(
      'include_benefits_in_comp_chart',
      data.formData.company.include_benefits_in_comp_chart ? 'true' : 'false'
    );
  }

  // Add use_gross_equity_value field
  if (typeof data.formData.company?.use_gross_equity_value === 'boolean') {
    formData.append(
      'use_gross_equity_value',
      data.formData.company.use_gross_equity_value ? 'true' : 'false'
    );
  }

  // Add comp-chart customization fields
  if (data.formData.company?.comp_chart_max_multiplier !== undefined) {
    formData.append(
      'comp_chart_max_multiplier',
      String(data.formData.company.comp_chart_max_multiplier)
    );
  }

  if (data.formData.company?.comp_chart_disclaimer !== undefined) {
    formData.append('comp_chart_disclaimer', data.formData.company.comp_chart_disclaimer);
  }

  // Add valuation field
  if (data.formData.company?.valuation !== undefined && data.formData.company?.valuation !== null) {
    formData.append('valuation', data.formData.company.valuation.toString());
  }

  // Add outstanding_shares field
  if (
    data.formData.company?.outstanding_shares !== undefined &&
    data.formData.company?.outstanding_shares !== null
  ) {
    formData.append('outstanding_shares', data.formData.company.outstanding_shares.toString());
  }

  // Add default equity settings (send empty to clear)
  if (data.formData.company?.default_equity_type !== undefined) {
    formData.append('default_equity_type', data.formData.company.default_equity_type ?? '');
  }

  if (data.formData.company?.default_strike_price !== undefined) {
    const v = data.formData.company.default_strike_price;
    formData.append('default_strike_price', v === null ? '' : String(v));
  }

  if (data.formData.company?.default_preferred_price !== undefined) {
    const v = data.formData.company.default_preferred_price;
    formData.append('default_preferred_price', v === null ? '' : String(v));
  }

  if (data.formData.company?.default_payment_frequency !== undefined) {
    formData.append(
      'default_payment_frequency',
      data.formData.company.default_payment_frequency ?? ''
    );
  }

  if (data.formData.company?.default_employment_type !== undefined) {
    formData.append('default_employment_type', data.formData.company.default_employment_type ?? '');
  }

  if (data.formData.company?.default_offer_expiration_days !== undefined) {
    const v = data.formData.company.default_offer_expiration_days;
    formData.append('default_offer_expiration_days', v === null ? '' : String(v));
  }

  if (data.formData.company?.default_welcome_message !== undefined) {
    formData.append('default_welcome_message', data.formData.company.default_welcome_message ?? '');
  }

  // Sent as JSON so the per-currency map survives the multipart encoding.
  if (data.formData.company?.default_conversion_rates !== undefined) {
    formData.append(
      'default_conversion_rates',
      JSON.stringify(data.formData.company.default_conversion_rates ?? {})
    );
  }

  // Add offer visibility settings for public shared links
  if (typeof data.formData.company?.public_offer_show_compensation === 'boolean') {
    formData.append(
      'public_offer_show_compensation',
      data.formData.company.public_offer_show_compensation ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_equity === 'boolean') {
    formData.append(
      'public_offer_show_equity',
      data.formData.company.public_offer_show_equity ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_benefits === 'boolean') {
    formData.append(
      'public_offer_show_benefits',
      data.formData.company.public_offer_show_benefits ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_bonus === 'boolean') {
    formData.append(
      'public_offer_show_bonus',
      data.formData.company.public_offer_show_bonus ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_commission === 'boolean') {
    formData.append(
      'public_offer_show_commission',
      data.formData.company.public_offer_show_commission ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_total_comp_chart === 'boolean') {
    formData.append(
      'public_offer_show_total_comp_chart',
      data.formData.company.public_offer_show_total_comp_chart ? 'true' : 'false'
    );
  }
  if (typeof data.formData.company?.public_offer_show_comparable_companies === 'boolean') {
    formData.append(
      'public_offer_show_comparable_companies',
      data.formData.company.public_offer_show_comparable_companies ? 'true' : 'false'
    );
  }

  if (data.formData.primary_color) {
    formData.append('primary_color', data.formData.primary_color);
  }

  // Handle file uploads
  if (data.logoFile) {
    formData.append('logo', data.logoFile);
  }

  if (data.headerImageFile) {
    formData.append('header_image', data.headerImageFile);
  }

  // Handle file removals
  if (data.removeLogo) {
    formData.append('remove_logo', 'true');
  }

  if (data.removeHeaderImage) {
    formData.append('remove_header_image', 'true');
  }

  const result = await request<CompanyBranding>('/company/branding/update/', {
    method: 'POST',
    body: formData,
  });
  return unwrapData(result);
};
